import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';

import { useRolesQuery } from '../hooks/use-roles';
import { RoleListTable } from './RoleListTable';

const SKELETON_ROWS = 5;

function RoleListSkeleton() {
  const { t } = useTranslation('roles');
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <tr style={{ background: 'var(--muted)' }}>
            <th style={{ width: 60 }}>{t('list.columns.uid')}</th>
            <th style={{ width: 220 }}>{t('list.columns.actions')}</th>
            <th>{t('list.columns.name')}</th>
            <th style={{ width: 180 }}>{t('list.columns.updatedAt')}</th>
            <th style={{ width: 180 }}>{t('list.columns.createdAt')}</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <tr key={i}>
              {Array.from({ length: 5 }).map((__, j) => (
                <td key={j}>
                  <div
                    className="animate-pulse"
                    style={{
                      height: 14,
                      background: 'var(--muted)',
                      borderRadius: 4,
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleListEmpty() {
  const { t } = useTranslation('roles');
  return (
    <div className="card">
      <div
        className="empty-state"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          padding: '48px 16px',
          color: 'var(--muted-foreground)',
        }}
      >
        <Shield size={48} />
        <p>{t('list.empty')}</p>
      </div>
    </div>
  );
}

function RoleListError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('roles');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 8,
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span>{t('list.loadError')}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('actions.retry')}
      </button>
    </div>
  );
}

export function RoleListView() {
  const { data, isLoading, error, refetch } = useRolesQuery();

  if (isLoading) return <RoleListSkeleton />;
  if (error) return <RoleListError onRetry={() => void refetch()} />;
  if (!data || data.length === 0) return <RoleListEmpty />;
  return <RoleListTable roles={data} />;
}
